import { defaultCollectionAuth, defaultRequestAuth } from '../requestAuth';

function oauth2Entry(key, value, type = 'string') {
  return { key, value: value ?? '', type };
}

/**
 * Reconstruct Postman's `{ query, variables }` (variables as a JSON *string*)
 * from our stored graphql body — a JSON string of `{ query, variables, operationName }`.
 */
function graphQLFromBody(bodyStr) {
  let query = '';
  let variables = '';
  try {
    const parsed = JSON.parse(bodyStr || '');
    if (parsed && typeof parsed === 'object') {
      query = typeof parsed.query === 'string' ? parsed.query : '';
      if (parsed.variables != null && parsed.variables !== '') {
        variables =
          typeof parsed.variables === 'string'
            ? parsed.variables
            : JSON.stringify(parsed.variables, null, 2);
      }
    }
  } catch {
    // Not JSON — treat the whole body as the query text.
    query = bodyStr || '';
  }
  return { query, variables };
}

// Common HTTP status reason phrases for Postman example `status` text.
const STATUS_TEXT = {
  200: 'OK', 201: 'Created', 202: 'Accepted', 204: 'No Content',
  301: 'Moved Permanently', 302: 'Found', 304: 'Not Modified',
  400: 'Bad Request', 401: 'Unauthorized', 403: 'Forbidden', 404: 'Not Found',
  409: 'Conflict', 422: 'Unprocessable Entity', 429: 'Too Many Requests',
  500: 'Internal Server Error', 502: 'Bad Gateway', 503: 'Service Unavailable',
};

const CONTENT_TYPE_PREVIEW_LANG = {
  'application/json': 'json',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'text/html': 'html',
  'text/plain': 'text',
  'application/javascript': 'javascript',
};

/** Map our mock `responseExamples` back to Postman saved examples (`item.response[]`). */
export function responseExamplesToPostman(request) {
  const examples = request?.mock?.responseExamples;
  if (!Array.isArray(examples) || examples.length === 0) return [];
  return examples.map((ex) => {
    const code = Number(ex.status) || 200;
    const contentType = ex.contentType || 'application/json';
    const header = (ex.headers || [])
      .filter((h) => h?.key)
      .map((h) => ({ key: h.key, value: h.value ?? '' }));
    if (!header.some((h) => h.key.toLowerCase() === 'content-type')) {
      header.push({ key: 'Content-Type', value: contentType });
    }
    return {
      name: ex.name || `Example ${code}`,
      status: STATUS_TEXT[code] || '',
      code,
      header,
      cookie: [],
      body: ex.body ?? '',
      _postman_previewlanguage:
        CONTENT_TYPE_PREVIEW_LANG[String(contentType).split(';')[0].trim()] || 'text',
    };
  });
}

export function mapAuthToPostman(auth, context = 'request', warnings) {
  if (!auth || auth.type === 'noauth') {
    return context === 'collection' ? undefined : { type: 'noauth' };
  }
  if (auth.type === 'inherit') {
    return { type: 'inherit' };
  }

  const type = String(auth.type || '').toLowerCase();

  switch (type) {
    case 'bearer':
      return {
        type: 'bearer',
        bearer: [oauth2Entry('token', auth.bearer)],
      };
    case 'basic':
      return {
        type: 'basic',
        basic: [
          oauth2Entry('username', auth.basic?.username),
          oauth2Entry('password', auth.basic?.password),
        ],
      };
    case 'apikey': {
      return {
        type: 'apikey',
        apikey: [
          oauth2Entry('key', auth.apikey?.key),
          oauth2Entry('value', auth.apikey?.value),
          oauth2Entry('in', auth.apikey?.in === 'query' ? 'query' : 'header'),
        ],
      };
    }
    case 'oauth2': {
      const o = auth.oauth2 || {};
      const grant =
        o.grantType === 'authorization_code_pkce'
          ? 'authorization_code_with_pkce'
          : o.grantType === 'client_credentials'
            ? 'client_credentials'
            : o.grantType === 'manual'
              ? 'implicit'
              : 'authorization_code';
      const entries = [
        oauth2Entry('grant_type', grant),
        oauth2Entry('authUrl', o.authUrl),
        oauth2Entry('accessTokenUrl', o.tokenUrl),
        oauth2Entry('clientId', o.clientId),
        oauth2Entry('clientSecret', o.clientSecret),
        oauth2Entry('scope', o.scope),
        oauth2Entry('redirect_uri', o.redirectUri),
        oauth2Entry('accessToken', o.accessToken),
        oauth2Entry('refreshToken', o.refreshToken),
        oauth2Entry('tokenType', o.tokenType || 'Bearer'),
      ].filter((e) => e.value !== '');
      return { type: 'oauth2', oauth2: entries };
    }
    default:
      warnings?.push(`Auth type "${type}" exported as noauth`);
      return context === 'collection' ? undefined : { type: 'noauth' };
  }
}

export function mapBodyToPostman(request, warnings) {
  const bodyType = request.body_type || request.bodyType;
  const uiType =
    bodyType === 'formdata' ? 'formdata' : bodyType === 'form' ? 'urlencoded' : bodyType;

  if (uiType === 'formdata') {
    const fields =
      request.settings?.form_data_fields ||
      request.formDataFields ||
      [];
    const formdata = fields
      .filter((f) => f?.key)
      .map((f) => {
        if (f.type === 'file') {
          if (!f.filePath) {
            warnings?.push(`File field "${f.key}" omitted (no file path)`);
            return null;
          }
          return {
            key: f.key,
            type: 'file',
            src: f.filePath,
            disabled: f.enabled === false,
          };
        }
        return {
          key: f.key,
          value: f.value ?? '',
          type: 'text',
          disabled: f.enabled === false,
        };
      })
      .filter(Boolean);
    return { mode: 'formdata', formdata };
  }

  if (uiType === 'urlencoded' || bodyType === 'form') {
    const fields = request.settings?.form_data_fields || request.formDataFields;
    if (Array.isArray(fields) && fields.length) {
      const urlencoded = fields
        .filter((f) => f?.key)
        .map((f) => ({
          key: f.key,
          value: f.value ?? '',
          type: 'text',
          disabled: f.enabled === false,
        }));
      return { mode: 'urlencoded', urlencoded };
    }
    // Fallback: serialized query string with no per-row enabled state.
    const params = new URLSearchParams(request.body || '');
    const urlencoded = [];
    for (const [key, value] of params.entries()) {
      urlencoded.push({ key, value, type: 'text', disabled: false });
    }
    return { mode: 'urlencoded', urlencoded };
  }

  if (bodyType === 'graphql') {
    return { mode: 'graphql', graphql: graphQLFromBody(request.body) };
  }

  if (bodyType === 'json') {
    return {
      mode: 'raw',
      raw: request.body || '',
      options: { raw: { language: 'json' } },
    };
  }

  if (bodyType === 'binary') {
    warnings?.push(`Binary body for "${request.name}" not exported`);
    return { mode: 'raw', raw: '' };
  }

  return {
    mode: 'raw',
    raw: request.body || '',
    options: { raw: { language: 'text' } },
  };
}

export function buildPostmanUrl(request) {
  const url = request.url || '';
  const params = (request.params || []).filter((p) => p.key);
  if (params.length === 0) {
    return url;
  }
  return {
    raw: url,
    query: params.map((p) => ({
      key: p.key,
      value: p.value ?? '',
      disabled: p.enabled === false,
    })),
  };
}

/**
 * Build a Postman `event[]` from a node's pre-request/test scripts (accepts both
 * the camelCase runtime fields and the snake_case persisted fields). `script.exec`
 * is the line-array form Postman expects.
 */
export function scriptsToPostmanEvents(node) {
  const pre = node?.preRequestScript ?? node?.pre_request_script ?? '';
  const test = node?.testScript ?? node?.test_script ?? '';
  const events = [];
  if (pre.trim()) {
    events.push({ listen: 'prerequest', script: { type: 'text/javascript', exec: pre.split('\n') } });
  }
  if (test.trim()) {
    events.push({ listen: 'test', script: { type: 'text/javascript', exec: test.split('\n') } });
  }
  return events;
}

export function mapRequestToPostmanItem(request, warnings) {
  const auth = request.auth || defaultRequestAuth();
  const postmanAuth =
    auth.type === 'inherit'
      ? { type: 'inherit' }
      : mapAuthToPostman(auth, 'request', warnings);

  const item = {
    name: request.name || 'Request',
    request: {
      method: (request.method || 'GET').toUpperCase(),
      header: (request.headers || [])
        .filter((h) => h.key)
        .map((h) => ({
          key: h.key,
          value: h.value ?? '',
          disabled: h.enabled === false,
        })),
      body: mapBodyToPostman(request, warnings),
      url: buildPostmanUrl(request),
      auth: postmanAuth,
      description: request.description || '',
    },
  };

  const events = scriptsToPostmanEvents(request);
  if (events.length) item.event = events;
  const responses = responseExamplesToPostman(request);
  if (responses.length) item.response = responses;
  if (request.protocolProfileBehavior && typeof request.protocolProfileBehavior === 'object') {
    item.protocolProfileBehavior = request.protocolProfileBehavior;
  }
  return item;
}

/**
 * Variable rows → Postman `variable` entries (collection AND folder rows).
 * A SECRET row is written without its value and typed `secret` (Postman's own
 * marker), so the file never carries it and re-imports as secret (F2).
 */
export function variableRowsToPostman(rows) {
  return (rows || [])
    .filter((v) => v?.key)
    .map((v) => ({
      key: v.key,
      value: v.secret ? '' : v.value ?? '',
      ...(v.secret ? { type: 'secret' } : {}),
      disabled: v.enabled === false,
    }));
}

export function variablesToPostman(settings) {
  return variableRowsToPostman(settings?.collection_variables || []);
}

export function collectionAuthToPostman(settings, warnings) {
  const auth = settings?.collection_auth || defaultCollectionAuth();
  if (!auth || auth.type === 'noauth') return undefined;
  return mapAuthToPostman(auth, 'collection', warnings);
}
